import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  DataTable,
  EmptyState,
  cn,
  type DataTableColumn,
  type DataTableSortState,
} from '@atrvd/saas-ui'
import { Building2, Plus } from 'lucide-react'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'
import { useTablePagination } from '@/hooks/useTablePagination'
import { queryErrorDisplay, tableQueryState } from '@/components/common/queryErrorDisplay'
import {
  FinancialAccountsSectionTitle,
  SECTION_EMPTY_STATE_CLASS,
} from '@/components/financial-accounts/FinancialAccountsSection'
import { ReadOnlyActionMenu, ReadOnlyTooltip } from '@/components/financial-accounts/ReadOnlyControls'
import {
  financialAccountsBasePath,
  type FacilityFinancialAccountsScope,
} from '@/components/financial-accounts/financialAccountsScope'
import { DeleteManualBankDialog } from './DeleteManualBankDialog'
import { ManualBankFormDialog, type ManualBankFormMode } from './ManualBankFormDialog'
import { MANUAL_BANK_COPY, accountsCountLabel } from './manualBankCopy'
import { signed, sortRows } from './manualBankSort'
import { compareText, manualBankPath, type ManualBankInScope } from './manualBanks.utils'
import { useManualBanksInScope } from './useManualBanksInScope'

const DEFAULT_SORT: DataTableSortState[] = [{ columnKey: 'displayName', direction: 'asc' }]

interface ManualBanksSectionProps {
  /** The facility the page is scoped to; null on the organization-wide page. */
  scope: FacilityFinancialAccountsScope | null
}

/**
 * Manual Financial Accounts — the list of manual banks on Financial Accounts
 * settings, mirroring the connections list above it.
 *
 * One row per bank (accounts sharing an institution name); a row opens the
 * bank's detail page. Sorting and paging are client-side: the whole list is in
 * memory, derived from the same account list the rest of the page uses. The
 * list is sorted here and then sliced, so DataTable (server sort mode) only
 * renders the current page; a sort change goes back to page 1.
 *
 * On a facility's page only the banks with an account of that facility are
 * listed and counted (see `manualBanksInScope`), new banks start at that
 * facility, and an inactive facility's list is read-only.
 */
export function ManualBanksSection({ scope }: ManualBanksSectionProps) {
  const navigate = useNavigate()
  const basePath = financialAccountsBasePath(scope)
  const readOnly = scope?.readOnly ?? false
  const { banks, isPending, isFetching, error, refreshError, refetch } = useManualBanksInScope(scope)

  // Local state, so leaving the page and coming back restores the default sort.
  const [sort, setSort] = useState<DataTableSortState[]>(DEFAULT_SORT)
  const [formMode, setFormMode] = useState<ManualBankFormMode | null>(null)
  /** Detail path of the bank awaiting delete confirmation — a bank's unique key. */
  const [deletePath, setDeletePath] = useState<string | null>(null)
  const deleteTarget = banks.find((bank) => manualBankPath(basePath, bank) === deletePath) ?? null

  const columns: DataTableColumn<ManualBankInScope>[] = useMemo(
    () => [
      {
        key: 'displayName',
        header: 'Institution',
        sortable: true,
        disableHide: true,
        cellClassName: 'font-semibold',
        sortFn: (a, b, direction) => signed(direction, compareText(a.displayName, b.displayName)),
        // A real button so the drill-down is reachable by keyboard (rows are
        // not focusable); it stops propagation so the row click does not
        // navigate a second time.
        render: (bank) => (
          <button
            type="button"
            className="w-full truncate text-left"
            onClick={(e) => {
              e.stopPropagation()
              navigate(manualBankPath(basePath, bank))
            }}
          >
            {bank.displayName}
          </button>
        ),
      },
      {
        key: 'accounts',
        header: 'Accounts & cards',
        sortable: true,
        // Equal counts fall back to the name, ascending in either direction.
        sortFn: (a, b, direction) =>
          signed(direction, a.accounts.length - b.accounts.length) ||
          compareText(a.displayName, b.displayName),
        render: (bank) => (
          <span className="text-sm text-slate-700">{accountsCountLabel(bank.accounts.length)}</span>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        width: '80px',
        disableHide: true,
        render: (bank) => {
          const name = bank.institutionName
          return (
            // Menu clicks bubble through the React tree to the row even though
            // the menu is portaled, so they are stopped here to keep the row
            // from navigating.
            <div
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <ReadOnlyActionMenu
                readOnly={readOnly}
                ariaLabel={`Actions for ${bank.displayName}`}
                items={[
                  // The unnamed group has no name to edit.
                  ...(name !== null
                    ? [
                        {
                          id: 'edit',
                          label: MANUAL_BANK_COPY.editBankCta,
                          onClick: () => setFormMode({ kind: 'edit', institutionName: name }),
                        },
                      ]
                    : []),
                  {
                    id: 'delete',
                    label: MANUAL_BANK_COPY.deleteBankCta,
                    variant: 'danger' as const,
                    onClick: () => setDeletePath(manualBankPath(basePath, bank)),
                  },
                ]}
              />
            </div>
          )
        },
      },
    ],
    [navigate, basePath, readOnly],
  )

  const columnPrefs = useTableColumnPrefs('manual-banks', columns)
  const pagination = useTablePagination({
    totalItems: isPending ? undefined : banks.length,
    resetKey: JSON.stringify(sort),
  })
  // Paged on the client from the full lists, so there is never placeholder data.
  const tableState = tableQueryState(
    { isPending, isPlaceholderData: false, isFetching, error: error ?? refreshError, refetch },
    banks.length,
  )
  const errorDisplay = queryErrorDisplay(tableState, banks.length)

  return (
    // A section, not a Card: DataTable draws its own card frame.
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <FinancialAccountsSectionTitle
          title={MANUAL_BANK_COPY.sectionTitle}
          subtitle={MANUAL_BANK_COPY.sectionSubtitle}
        />
        <ReadOnlyTooltip readOnly={readOnly}>
          <Button
            onClick={() =>
              setFormMode({ kind: 'create', initialName: '', defaultFacilityId: scope?.facilityId ?? null })
            }
            disabled={readOnly}
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            {MANUAL_BANK_COPY.addBankCta}
          </Button>
        </ReadOnlyTooltip>
      </div>

      {errorDisplay.banner}
      {!tableState.loading && !tableState.error && banks.length === 0 ? (
        // Not the DataTable's own empty state: that keeps the header row and the
        // Columns control, and an empty section offers nothing to sort or configure.
        // Framed like the table it stands in for.
        <EmptyState
          icon={Building2}
          title={scope ? MANUAL_BANK_COPY.facilityEmptyTitle : MANUAL_BANK_COPY.emptyTitle}
          description={MANUAL_BANK_COPY.emptyDescription}
          className={cn('rounded-lg border bg-card', SECTION_EMPTY_STATE_CLASS)}
        />
      ) : (
        <DataTable<ManualBankInScope>
          columns={columns}
          data={pagination.paginate(sortRows(banks, columns, sort))}
          rowKey={(bank) => manualBankPath(basePath, bank)}
          onRowClick={(bank) => navigate(manualBankPath(basePath, bank))}
          pagination={pagination.dataTablePagination}
          loading={tableState.loading}
          sorting={{
            mode: 'server',
            sortState: sort,
            // The third click of a header clears the sort: back to the default order.
            onSortChange: (next) => setSort(next.length > 0 ? next : DEFAULT_SORT),
          }}
          error={errorDisplay.tableError}
          itemName="financial accounts"
          showColumnControls
          enableColumnReorder
          {...columnPrefs}
        />
      )}

      {formMode && <ManualBankFormDialog mode={formMode} onClose={() => setFormMode(null)} />}
      <DeleteManualBankDialog bank={deleteTarget} onClose={() => setDeletePath(null)} />
    </section>
  )
}
